/**
 * FreemiumService -- Smart freemium with usage-based triggers.
 *
 * Manages plan state, trial periods, usage tracking, and contextual
 * upgrade prompts. Designed to feel GENEROUS -- never interrupts
 * gameplay, reviews, or prayer times.
 */

// ---------- Types ----------

export type Plan = 'free' | 'individual' | 'family' | 'supporter';

export interface FreemiumLimits {
  dailyGames: number;
  dailyReviews: number;
  offlineAccess: boolean;
  communityCreate: boolean;
  aiChatMessages: number;
  familyProfiles: number;
}

export interface UsageCounters {
  gamesPlayed: number;
  cardsReviewed: number;
  articlesRead: number;
  aiChatMessages: number;
}

export interface FreemiumState {
  plan: Plan;
  trialStartDate: string;
  trialDaysRemaining: number;
  usageThisMonth: UsageCounters;
  usageToday: UsageCounters;
  limits: FreemiumLimits;
  showUpgradePrompt: boolean;
}

export type UpgradeReason =
  | 'daily_games_limit'
  | 'daily_reviews_limit'
  | 'daily_chat_limit'
  | 'offline_access'
  | 'community_create'
  | 'family_profiles'
  | 'power_user'
  | 'feature_locked'
  | null;

// ---------- Constants ----------

const LS_PREFIX = 'aweni_freemium_';
const LS_USAGE_KEY = LS_PREFIX + 'usage_today';
const LS_USAGE_DATE_KEY = LS_PREFIX + 'usage_date';
const LS_MONTHLY_KEY = LS_PREFIX + 'usage_month';
const LS_MONTHLY_DATE_KEY = LS_PREFIX + 'usage_month_date';
const LS_TRIAL_KEY = LS_PREFIX + 'trial_start';
const LS_PLAN_KEY = LS_PREFIX + 'plan';
const LS_PROMPT_DISMISS_KEY = LS_PREFIX + 'prompt_dismissed';

const TRIAL_DAYS = 60; // 2 months

const PLAN_LIMITS: Record<Plan, FreemiumLimits> = {
  free: {
    dailyGames: 5,
    dailyReviews: 20,
    offlineAccess: false,
    communityCreate: false,
    aiChatMessages: 10,
    familyProfiles: 1,
  },
  individual: {
    dailyGames: Infinity,
    dailyReviews: Infinity,
    offlineAccess: true,
    communityCreate: true,
    aiChatMessages: Infinity,
    familyProfiles: 1,
  },
  family: {
    dailyGames: Infinity,
    dailyReviews: Infinity,
    offlineAccess: true,
    communityCreate: true,
    aiChatMessages: Infinity,
    familyProfiles: 6,
  },
  supporter: {
    dailyGames: Infinity,
    dailyReviews: Infinity,
    offlineAccess: true,
    communityCreate: true,
    aiChatMessages: Infinity,
    familyProfiles: Infinity,
  },
};

const PLAN_PRICES: Record<Plan, number> = {
  free: 0,
  individual: 4.99,
  family: 9.99,
  supporter: 19.99,
};

// ---------- Helpers ----------

function getTodayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function getMonthKey(): string {
  return new Date().toISOString().slice(0, 7);
}

function getDefaultUsage(): UsageCounters {
  return { gamesPlayed: 0, cardsReviewed: 0, articlesRead: 0, aiChatMessages: 0 };
}

// ---------- Service ----------

export class FreemiumService {
  private _plan: Plan = 'free';
  private _trialStartDate: string | null = null;
  private _usageToday: UsageCounters;
  private _usageMonth: UsageCounters;
  private _apiBase: string;
  private _getToken: () => string | null;
  private _promptDismissedUntil = 0;

  constructor(apiBase: string, getToken: () => string | null) {
    this._apiBase = apiBase;
    this._getToken = getToken;
    this._usageToday = getDefaultUsage();
    this._usageMonth = getDefaultUsage();
    this._loadFromStorage();
  }

  // ---------- Initialization ----------

  async init(): Promise<void> {
    // Try to get plan from server (JWT claims or preferences endpoint)
    const token = this._getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.plan) {
          this._plan = payload.plan as Plan;
          this._savePlan();
        }
      } catch { /* JWT decode failure -- use cached plan */ }

      // Fetch latest plan from server
      try {
        const res = await fetch(`${this._apiBase}/preferences?key=subscription`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.value?.plan) {
            this._plan = data.value.plan as Plan;
            this._savePlan();
          }
          if (data?.value?.trial_start) {
            this._trialStartDate = data.value.trial_start;
            try { localStorage.setItem(LS_TRIAL_KEY, this._trialStartDate!); } catch { /* quota */ }
          }
        }
      } catch { /* offline -- use cached */ }

      // Sync daily usage to server (fire-and-forget)
      this._syncUsageToServer();
    }
  }

  // ---------- State ----------

  get plan(): Plan {
    return this._plan;
  }

  get isTrialActive(): boolean {
    return this.trialDaysRemaining > 0;
  }

  get trialDaysRemaining(): number {
    if (!this._trialStartDate) return 0;
    const start = new Date(this._trialStartDate).getTime();
    const now = Date.now();
    const elapsed = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    return Math.max(0, TRIAL_DAYS - elapsed);
  }

  get limits(): FreemiumLimits {
    // During trial, everything is unlimited
    if (this.isTrialActive) return PLAN_LIMITS.supporter;
    return PLAN_LIMITS[this._plan];
  }

  get effectivePlan(): Plan {
    if (this.isTrialActive) return 'supporter';
    return this._plan;
  }

  getState(): FreemiumState {
    return {
      plan: this._plan,
      trialStartDate: this._trialStartDate || '',
      trialDaysRemaining: this.trialDaysRemaining,
      usageThisMonth: { ...this._usageMonth },
      usageToday: { ...this._usageToday },
      limits: this.limits,
      showUpgradePrompt: this._shouldShowPrompt(),
    };
  }

  static getPlanPrice(plan: Plan): number {
    return PLAN_PRICES[plan];
  }

  static getPlanLimits(plan: Plan): FreemiumLimits {
    return PLAN_LIMITS[plan];
  }

  // ---------- Usage Checking ----------

  canPlayGame(): boolean {
    if (this.isTrialActive || this._plan !== 'free') return true;
    return this._usageToday.gamesPlayed < this.limits.dailyGames;
  }

  canReviewCards(): boolean {
    if (this.isTrialActive || this._plan !== 'free') return true;
    return this._usageToday.cardsReviewed < this.limits.dailyReviews;
  }

  canUseChatAI(): boolean {
    if (this.isTrialActive || this._plan !== 'free') return true;
    return this._usageToday.aiChatMessages < this.limits.aiChatMessages;
  }

  canCreateCommunity(): boolean {
    if (this.isTrialActive) return true;
    return this.limits.communityCreate;
  }

  canAccessOffline(): boolean {
    if (this.isTrialActive) return true;
    return this.limits.offlineAccess;
  }

  getMaxFamilyProfiles(): number {
    if (this.isTrialActive) return Infinity;
    return this.limits.familyProfiles;
  }

  getUpgradeReason(): UpgradeReason {
    if (this.isTrialActive || this._plan !== 'free') return null;

    if (this._usageToday.gamesPlayed >= this.limits.dailyGames) return 'daily_games_limit';
    if (this._usageToday.cardsReviewed >= this.limits.dailyReviews) return 'daily_reviews_limit';
    if (this._usageToday.aiChatMessages >= this.limits.aiChatMessages) return 'daily_chat_limit';

    // Power user detection: used AWENI > 3 times today across features
    const totalActions = this._usageToday.gamesPlayed + this._usageToday.cardsReviewed +
                         this._usageToday.articlesRead + this._usageToday.aiChatMessages;
    if (totalActions >= 10) return 'power_user';

    return null;
  }

  // ---------- Usage Recording ----------

  recordGamePlayed(): void {
    this._usageToday.gamesPlayed++;
    this._usageMonth.gamesPlayed++;
    this._saveUsage();
  }

  recordCardReviewed(): void {
    this._usageToday.cardsReviewed++;
    this._usageMonth.cardsReviewed++;
    this._saveUsage();
  }

  recordArticleRead(): void {
    this._usageToday.articlesRead++;
    this._usageMonth.articlesRead++;
    this._saveUsage();
  }

  recordChatMessage(): void {
    this._usageToday.aiChatMessages++;
    this._usageMonth.aiChatMessages++;
    this._saveUsage();
  }

  // ---------- Upgrade Prompts ----------

  /**
   * Returns true if we should show an upgrade prompt now.
   * NEVER during gameplay, review, or prayer. Only at natural breaks.
   */
  private _shouldShowPrompt(): boolean {
    if (this._plan !== 'free') return false;
    if (this.isTrialActive) return false;
    if (this._promptDismissedUntil > Date.now()) return false;

    const reason = this.getUpgradeReason();
    return reason !== null;
  }

  dismissPrompt(durationMs = 24 * 60 * 60 * 1000): void {
    this._promptDismissedUntil = Date.now() + durationMs;
    try {
      localStorage.setItem(LS_PROMPT_DISMISS_KEY, String(this._promptDismissedUntil));
    } catch { /* quota */ }
  }

  /**
   * Start a trial (called from upgrade page).
   */
  async startTrial(): Promise<boolean> {
    const token = this._getToken();
    if (!token) return false;

    try {
      const res = await fetch(`${this._apiBase}/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          key: 'subscription',
          value: { plan: this._plan, trial_start: new Date().toISOString() },
        }),
      });

      if (res.ok) {
        this._trialStartDate = new Date().toISOString();
        try { localStorage.setItem(LS_TRIAL_KEY, this._trialStartDate); } catch { /* quota */ }
        return true;
      }
    } catch { /* network error */ }
    return false;
  }

  /**
   * Get Stripe checkout URL for a plan.
   */
  getCheckoutUrl(plan: Plan): string {
    return `/identity/stripe/checkout?plan=${plan}`;
  }

  /**
   * Get donation URL.
   */
  getDonationUrl(amount?: number): string {
    return amount
      ? `/identity/stripe/donation?amount=${amount}`
      : '/identity/stripe/donation';
  }

  // ---------- Persistence ----------

  private _loadFromStorage(): void {
    try {
      // Plan
      const savedPlan = localStorage.getItem(LS_PLAN_KEY);
      if (savedPlan && ['free', 'individual', 'family', 'supporter'].includes(savedPlan)) {
        this._plan = savedPlan as Plan;
      }

      // Trial
      this._trialStartDate = localStorage.getItem(LS_TRIAL_KEY);

      // Today's usage
      const savedDate = localStorage.getItem(LS_USAGE_DATE_KEY);
      if (savedDate === getTodayKey()) {
        const raw = localStorage.getItem(LS_USAGE_KEY);
        if (raw) this._usageToday = { ...getDefaultUsage(), ...JSON.parse(raw) };
      } else {
        // New day -- reset
        localStorage.setItem(LS_USAGE_DATE_KEY, getTodayKey());
        this._usageToday = getDefaultUsage();
      }

      // Monthly usage
      const savedMonth = localStorage.getItem(LS_MONTHLY_DATE_KEY);
      if (savedMonth === getMonthKey()) {
        const raw = localStorage.getItem(LS_MONTHLY_KEY);
        if (raw) this._usageMonth = { ...getDefaultUsage(), ...JSON.parse(raw) };
      } else {
        localStorage.setItem(LS_MONTHLY_DATE_KEY, getMonthKey());
        this._usageMonth = getDefaultUsage();
      }

      // Prompt dismiss
      const dismissed = localStorage.getItem(LS_PROMPT_DISMISS_KEY);
      if (dismissed) this._promptDismissedUntil = Number(dismissed);
    } catch {
      this._usageToday = getDefaultUsage();
      this._usageMonth = getDefaultUsage();
    }
  }

  private _saveUsage(): void {
    try {
      localStorage.setItem(LS_USAGE_DATE_KEY, getTodayKey());
      localStorage.setItem(LS_USAGE_KEY, JSON.stringify(this._usageToday));
      localStorage.setItem(LS_MONTHLY_DATE_KEY, getMonthKey());
      localStorage.setItem(LS_MONTHLY_KEY, JSON.stringify(this._usageMonth));
    } catch { /* quota */ }
  }

  private _savePlan(): void {
    try { localStorage.setItem(LS_PLAN_KEY, this._plan); } catch { /* quota */ }
  }

  private async _syncUsageToServer(): Promise<void> {
    const token = this._getToken();
    if (!token) return;

    try {
      await fetch(`${this._apiBase}/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          key: 'usage_sync',
          value: {
            date: getTodayKey(),
            today: this._usageToday,
            month: getMonthKey(),
            monthly: this._usageMonth,
          },
        }),
      });
    } catch { /* fire-and-forget */ }
  }

  // ---------- Plan Update (after payment callback) ----------

  updatePlan(plan: Plan): void {
    this._plan = plan;
    this._savePlan();
  }
}

// ---------- Singleton ----------

let _instance: FreemiumService | null = null;

export function getFreemiumService(): FreemiumService | null {
  return _instance;
}

export function initFreemiumService(
  apiBase: string,
  getToken: () => string | null,
): FreemiumService {
  _instance = new FreemiumService(apiBase, getToken);
  return _instance;
}
